import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { opportunites, type OpportuniteType } from "@/mocks/opportunites";
import SectionHeader from "@/components/base/SectionHeader";

export default function OpportunitesSection() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("fr") ? "fr" : "en";
  const [activeFilter, setActiveFilter] = useState<OpportuniteType | "all">("all");

  const typeConfig: Record<OpportuniteType, { labelKey: string; icon: string; bg: string; text: string; border: string }> = {
    job:     { labelKey: "opportunites.type.job",     icon: "ri-briefcase-line",             bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
    funding: { labelKey: "opportunites.type.funding", icon: "ri-money-dollar-circle-line",   bg: "bg-orange-50",  text: "text-orange-600",  border: "border-orange-200" },
    appel:   { labelKey: "opportunites.type.appel",   icon: "ri-trophy-line",                bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200" },
  };

  const filters: { key: OpportuniteType | "all"; labelKey: string }[] = [
    { key: "all",     labelKey: "opportunites.filter.all" },
    { key: "funding", labelKey: "opportunites.filter.funding" },
    { key: "job",     labelKey: "opportunites.filter.job" },
    { key: "appel",   labelKey: "opportunites.filter.appel" },
  ];

  const filtered = activeFilter === "all" ? opportunites : opportunites.filter((o) => o.type === activeFilter);
  const displayed = filtered.slice(0, 6);

  return (
    <section className="py-16 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <SectionHeader
          title={t("section.opportunites.title")}
          subtitle={t("section.opportunites.subtitle")}
          viewAllLink="/opportunites"
          accent="orange"
        />

        <div className="flex gap-2 mb-8 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeFilter === f.key ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {displayed.map((opp) => {
            const cfg = typeConfig[opp.type];
            const daysLeft = Math.ceil((new Date(opp.deadline).getTime() - Date.now()) / 86400000);
            const isUrgent = daysLeft <= 14;

            return (
              <a
                key={opp.id}
                href={opp.link}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className={`group bg-white rounded-2xl border ${opp.featured ? "border-orange-200" : "border-gray-100"} hover:border-orange-300 hover:-translate-y-1 transition-all duration-300 p-5 cursor-pointer block`}
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                      <img src={opp.logo} alt={opp.organization} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-700 line-clamp-1">{opp.organization}</p>
                      <p className="text-xs text-gray-400">{opp.countryFlag} {opp.country}</p>
                    </div>
                  </div>
                  <span className={`flex-shrink-0 flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                    <i className={cfg.icon}></i>
                    {t(cfg.labelKey)}
                  </span>
                </div>

                <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2 mb-2 group-hover:text-orange-500 transition-colors">{opp.title}</h3>
                <p className="text-xs text-gray-500 line-clamp-2 mb-4">{opp.description}</p>

                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-3">
                    {opp.amount && (
                      <span className="text-xs font-bold text-green-600 flex items-center gap-1">
                        <i className="ri-money-dollar-circle-line"></i>{opp.amount}
                      </span>
                    )}
                    {opp.remote && (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <i className="ri-global-line"></i>{t("opportunites.remote")}
                      </span>
                    )}
                  </div>
                  <span className={`text-xs font-semibold flex items-center gap-1 ${isUrgent ? "text-red-500" : "text-gray-400"}`}>
                    <i className="ri-time-line"></i>
                    {isUrgent ? `J-${daysLeft}` : new Date(opp.deadline).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { day: "2-digit", month: "short" })}
                  </span>
                </div>
              </a>
            );
          })}
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/opportunites"
            className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-semibold px-8 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-compass-3-line"></i>
            {t("opportunites.viewAll")}
          </Link>
        </div>
      </div>
    </section>
  );
}
